<template>
  <aside class="sidebar" :class="{ mini: collapsed }">
    <div class="sidebar-section">
      <div class="nav-section-title">{{ t('nav.section_main') }}</div>
      <router-link
        v-for="item in mainItems"
        :key="item.to"
        :to="item.to"
        class="nav-item"
        :class="{ active: isActive(item.to) }"
      >
        <AppIcon :name="item.icon" :size="18" />
        <span class="nav-label">{{ item.label }}</span>
      </router-link>
    </div>

    <div class="sidebar-section">
      <div class="nav-section-title">{{ t('nav.section_games') }}</div>
      <router-link
        v-for="cat in gameCategories"
        :key="cat.to"
        :to="cat.to"
        class="nav-item"
        :class="{ active: isActive(cat.to) }"
      >
        <AppIcon :name="cat.icon" :size="18" />
        <span class="nav-label">{{ cat.label }}</span>
      </router-link>
    </div>

    <div class="sidebar-section">
      <div class="nav-section-title">{{ t('nav.section_player') }}</div>
      <router-link
        v-for="item in playerItems"
        :key="item.to"
        :to="item.to"
        class="nav-item"
        :class="{ active: isActive(item.to) }"
      >
        <AppIcon :name="item.icon" :size="18" />
        <span class="nav-label">{{ item.label }}</span>
      </router-link>
    </div>

    <div class="sidebar-section">
      <div class="nav-section-title">{{ t('nav.section_account') }}</div>
      <router-link
        v-for="item in accountItems"
        :key="item.to"
        :to="item.to"
        class="nav-item"
        :class="{ active: isActive(item.to) }"
      >
        <AppIcon :name="item.icon" :size="18" />
        <span class="nav-label">{{ item.label }}</span>
      </router-link>
    </div>

    <div class="sidebar-footer">
      <button
        class="sidebar-collapse-btn"
        @click="toggle"
        :aria-label="collapsed ? t('nav.expand_sidebar') : t('nav.collapse_sidebar')"
      >
        <ChevronsLeft class="collapse-icon" :class="{ flipped: collapsed }" :size="16" />
      </button>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import AppIcon from '@/components/AppIcon.vue'
import { ChevronsLeft } from 'lucide-vue-next'

const route = useRoute()
const { t } = useI18n()
const collapsed = ref(false)

const emit = defineEmits<{
  (e: 'toggle', collapsed: boolean): void
}>()

function toggle() {
  collapsed.value = !collapsed.value
  emit('toggle', collapsed.value)
}

function isActive(path: string): boolean {
  return route.path === path || route.path.startsWith(path + '/')
}

const mainItems = computed(() => [
  { icon: 'nav-casino', label: t('nav.casino'), to: '/player/lobby' },
  // Live Casino — reserved for when a live dealer provider is integrated
])

const gameCategories = computed(() => [
  { icon: 'category-slots', label: t('nav.slots'), to: '/player/games/slots' },
  { icon: 'category-table', label: t('nav.table_games'), to: '/player/games/table' },
  { icon: 'category-crash', label: t('nav.crash'), to: '/player/games/crash' },
  { icon: 'category-originals', label: t('nav.originals'), to: '/player/games/originals' },
])

const playerItems = computed(() => [
  { icon: 'nav-promotions', label: t('nav.promotions'), to: '/player/bonuses' },
  { icon: 'nav-gem', label: t('nav.loyalty_shop'), to: '/player/shop' },
  { icon: 'nav-vip', label: t('nav.vip'), to: '/player/vip' },
  { icon: 'nav-leaderboard', label: t('nav.leaderboard'), to: '/player/leaderboard' },
  { icon: 'nav-affiliates', label: t('nav.affiliates'), to: '/player/affiliates' },
])

const accountItems = computed(() => [
  { icon: 'nav-user', label: t('nav.profile'), to: '/player/profile' },
  { icon: 'nav-wallet', label: t('nav.wallet'), to: '/player/wallet' },
  { icon: 'nav-history', label: t('nav.history'), to: '/player/history' },
])
</script>

<style scoped>
.sidebar {
  width: var(--sidebar-width, 240px);
  background: var(--bg-sidebar, #0b1520);
  border-right: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  overflow-y: auto;
  overflow-x: hidden;
  transition: width var(--transition-normal, 250ms ease);
  position: relative;
  z-index: 100;
}

.sidebar.mini {
  width: var(--sidebar-collapsed, 64px);
}

.sidebar.mini .nav-label,
.sidebar.mini .nav-section-title,
.sidebar.mini .nav-chevron {
  display: none;
}

.sidebar.mini .nav-item {
  justify-content: center;
  padding: 0;
}

.sidebar.mini .nav-item .nav-icon {
  margin: 0;
}

/* Sections */
.sidebar-section {
  padding: 8px 8px 4px;
}

.nav-section-title {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-tertiary, #7f8fa4);
  padding: 8px 10px 4px;
}

/* Nav items */
.nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 10px;
  height: 40px;
  border-radius: var(--radius-md, 8px);
  cursor: pointer;
  transition: all var(--transition-fast, 150ms ease);
  position: relative;
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  border: none;
  width: 100%;
  text-align: left;
  text-decoration: none;
  background: none;
}

.nav-item:hover {
  background: var(--bg-tertiary, #213743);
  color: var(--text-primary, #fff);
}

.nav-item.active {
  background: rgba(0, 231, 1, 0.08);
  color: var(--text-primary, #fff);
  border-left: 3px solid var(--accent-primary, #00e701);
}

.nav-item.active .nav-icon {
  color: var(--accent-primary, #00e701);
}

.nav-icon {
  font-size: 16px;
  flex-shrink: 0;
  width: 20px;
  text-align: center;
}

.nav-label {
  flex: 1;
}

/* Footer with collapse button */
.sidebar-footer {
  margin-top: auto;
  padding: 8px;
}

.sidebar-collapse-btn {
  width: 100%;
  padding: 8px;
  background: var(--bg-tertiary, #213743);
  border-radius: var(--radius-md, 8px);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 16px;
  transition: all var(--transition-fast, 150ms ease);
  border: none;
}

.sidebar-collapse-btn:hover {
  color: var(--text-primary, #fff);
  background: var(--bg-hover, #2a4455);
}

.collapse-icon {
  display: inline-block;
  transition: transform var(--transition-normal, 250ms ease);
}

.collapse-icon.flipped {
  transform: rotate(180deg);
}

/* Responsive: hide sidebar on mobile */
@media (max-width: 768px) {
  .sidebar {
    display: none;
  }
}

/* RTL Support */
/* In RTL the sidebar is visually on the right (flex-direction reverses). Switch border side. */
[dir='rtl'] .sidebar {
  border-right: none;
  border-left: 1px solid rgba(255, 255, 255, 0.05);
}

[dir='rtl'] .nav-item {
  text-align: right;
}

/* Active indicator: move from left edge to right edge in RTL */
[dir='rtl'] .nav-item.active {
  border-left: none;
  border-right: 3px solid var(--accent-primary, #00e701);
}

/* Collapse icon: in RTL sidebar is on the right, so flip direction */
[dir='rtl'] .collapse-icon {
  transform: rotate(180deg);
}

[dir='rtl'] .collapse-icon.flipped {
  transform: rotate(0deg);
}
</style>
